import type { ClaimCandidate, ClaimCandidateStatus } from "@hp/types";
import { rollupGroupValues } from "./claimValue";

const STATUS_RANK: Record<ClaimCandidateStatus, number> = {
  rejected: -1,
  identified: 0,
  deferred: 1,
  validated: 2,
  filed: 3,
  allowed: 4,
  recovered: 5,
};

/**
 * One recoverable claim per disallowance + clause (all findings / FY tranches),
 * or - for a claim anchored on an entitlement gap rather than a disallowance -
 * one per agreement + head + forum, so a ten-year unclaimed entitlement is one
 * pot, not ten rows. Mirrors `claim_dedup.commercial_group_key` in the backend.
 */
export function claimCandidateGroupKey(candidate: ClaimCandidate): string {
  if (candidate.disallowance_id) {
    return [candidate.disallowance_id, candidate.matched_clause_id ?? "", candidate.recommended_forum].join("|");
  }
  if (candidate.entitlement_gap_id && candidate.entitlement_agreement_id && candidate.entitlement_head) {
    return ["gap", candidate.entitlement_agreement_id, candidate.entitlement_head, candidate.recommended_forum].join("|");
  }
  return candidate.id;
}

function representativeRank(candidate: ClaimCandidate): [number, number] {
  return [STATUS_RANK[candidate.status] ?? 0, candidate.confidence_score];
}

export interface ClaimCandidateGroup {
  key: string;
  primary: ClaimCandidate;
  members: ClaimCandidate[];
  valueLow: number;
  valueHigh: number;
  principalHigh: number;
  carryingHigh: number;
  memberCount: number;
}

function memberFromSummary(
  primary: ClaimCandidate,
  summary: NonNullable<ClaimCandidate["group_members"]>[number],
): ClaimCandidate {
  return {
    ...primary,
    id: summary.id,
    issue_tag: summary.issue_tag,
    finding_case_number: summary.finding_case_number ?? null,
    finding_id: summary.finding_id ?? primary.finding_id,
    financial_year: summary.financial_year ?? null,
    value_low: summary.value_low ?? primary.value_low,
    value_high: summary.value_high ?? primary.value_high,
    value_breakdown: summary.value_breakdown ?? primary.value_breakdown,
    status: summary.status ?? primary.status,
  };
}

function groupRollup(primary: ClaimCandidate, members: ClaimCandidate[]) {
  const rollup = rollupGroupValues(
    members.map((member) => ({
      id: member.id,
      issue_tag: member.issue_tag,
      finding_case_number: member.finding_case_number,
      finding_id: member.finding_id,
      financial_year: member.financial_year,
      value_low: member.value_low,
      value_high: member.value_high,
      value_breakdown: member.value_breakdown,
      status: member.status,
    })),
    primary.group_value_rollup,
  );
  return rollup;
}

/** Prefer API grouping; fall back to client-side grouping for older responses. */
export function groupClaimCandidates(candidates: ClaimCandidate[]): ClaimCandidateGroup[] {
  if (candidates.some((candidate) => candidate.group_members && candidate.group_members.length > 0)) {
    return candidates.map((primary) => {
      const members =
        primary.group_members && primary.group_members.length > 0
          ? primary.group_members.map((summary) => memberFromSummary(primary, summary))
          : [primary];
      const rollup = groupRollup(primary, members);
      return {
        key: primary.id,
        primary,
        members,
        valueLow: rollup.total_low,
        valueHigh: rollup.total_high,
        principalHigh: rollup.principal_high,
        carryingHigh: rollup.carrying_cost_high,
        memberCount: rollup.member_count,
      };
    });
  }

  const buckets = new Map<string, ClaimCandidate[]>();
  for (const candidate of candidates) {
    const key = claimCandidateGroupKey(candidate);
    const group = buckets.get(key) ?? [];
    group.push(candidate);
    buckets.set(key, group);
  }

  return [...buckets.values()].map((members) => {
    const sorted = [...members].sort((a, b) => {
      const [rankA, confA] = representativeRank(a);
      const [rankB, confB] = representativeRank(b);
      if (rankB !== rankA) return rankB - rankA;
      return confB - confA;
    });
    const primary = sorted[0];
    const rollup = groupRollup(primary, sorted);
    return {
      key: claimCandidateGroupKey(primary),
      primary,
      members: sorted,
      valueLow: rollup.total_low,
      valueHigh: rollup.total_high,
      principalHigh: rollup.principal_high,
      carryingHigh: rollup.carrying_cost_high,
      memberCount: rollup.member_count,
    };
  });
}

export function groupHasMixedStatus(members: ClaimCandidate[]): boolean {
  if (members.length <= 1) return false;
  const statuses = new Set(members.map((member) => member.status));
  return statuses.size > 1;
}

export function memberLabel(candidate: ClaimCandidate): string {
  const issue = candidate.issue_tag.replace(/_/g, " ");
  if (candidate.financial_year) {
    return `${issue} · ${candidate.financial_year}`;
  }
  if (candidate.finding_case_number) {
    return `${issue} · ${candidate.finding_case_number}`;
  }
  return `${issue} · ${candidate.finding_id.slice(0, 8)}`;
}
